/* Treatment lists (Alle behandlinger, the categories, Skinlab): skin needs as one sticky row of chips, and a result
   line with the live count, Sorter and search. Liquid renders every card; this script only shows, hides and orders them.
   The page's main list (data-filter-url) keeps its state in the address with the Shop's names: ?behov, ?sok and
   ?sorter; a Laser facials type is its own collection page. Choices replace the history entry (as in Shop), so Back
   from a treatment, reload and shared links show the same view. The chosen need follows the category tabs. */
if (!customElements.get('laserlab-treatment-filter')) {
  customElements.define('laserlab-treatment-filter', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const q = selector => this.querySelector(selector);
      this.count = q('[data-filter-count]');
      this.bar = q('[data-filter-bar]');
      this.form = q('[data-filter-form]');
      this.input = q('[data-filter-input]');
      this.clear = q('[data-filter-clear]');
      this.hint = q('[data-filter-hint]');
      this.hintText = this.hint?.textContent.trim() || '';
      this.searchToggle = q('[data-filter-search-toggle]');
      this.scroller = q('[data-filter-chips]');
      this.needChips = Array.from(this.querySelectorAll('[data-filter-need-chip]'));
      this.typeChips = Array.from(this.querySelectorAll('[data-filter-type-chip]'));
      this.typeAllURL = q('[data-filter-chips]')?.dataset.allUrl || '';
      this.sortSelect = q('[data-filter-sort]');
      this.sortCaption = q('[data-filter-sort-caption]');
      this.meta = q('[data-filter-meta]');
      // Search sits at the far right of the sticky chip row (or, without the row, in the result line).
      this.searchHost = this.form?.closest('[data-filter-bar]') || this.meta;
      this.empty = q('[data-filter-empty]');
      // The consultation card (not a filter card): first in the plain list on tablets and desktop; last on phones and
      // whenever a need, type or search is chosen, so the treatments lead.
      this.consult = q('.ll-treatment-browser__item--consult');
      this.phoneMedia = window.matchMedia('(max-width: 767.98px)');
      this.tabs = Array.from(this.querySelectorAll('[data-tab-needs]'));
      this.syncURL = this.dataset.filterUrl === 'true';
      this.sortCaptions = { recommended: 'Anbefalt', 'price-ascending': 'Laveste pris', 'price-descending': 'Høyeste pris' };
      if (!this.count || !(this.input || this.needChips.length || this.typeChips.length || this.sortSelect)) {
        this.removeAttribute('data-restoring');
        return;
      }

      // Need names and their short lines come from the chips (one source: snippets/laserlab-shop-need.liquid).
      this.needLabels = {};
      this.needWords = {};
      this.needChips.forEach(chip => {
        const value = chip.dataset.filterNeedChip;
        const label = chip.querySelector('[data-filter-need-label]')?.textContent.trim() || value;
        this.needLabels[value] = label;
        this.needWords[value] = `${label} ${chip.dataset.description || ''}`;
      });
      this.needs = Object.keys(this.needLabels);

      this.cards = Array.from(this.querySelectorAll('[data-filter-card]')).map((card, index) => {
        let tags = [];
        try { tags = JSON.parse(card.dataset.filterTags || '[]'); } catch (_) { /* Cards without tags remain searchable. */ }
        const labels = Array.isArray(tags) ? tags.map(tag => String(tag)).filter(tag => !/^behov-/i.test(tag)).map(tag => tag.replace(/^tag__[^_]+_/, '')) : [];
        const needs = (card.dataset.needs || '').split(' ').filter(Boolean);
        const visibleCopy = Array.from(card.querySelectorAll('.product-card__title, .product-card__excerpt, .ll-treatment-card__family, .llt__new-heading, .llt__new-copy, .llt__new-badge'))
          .map(node => node.textContent).join(' ');
        // Needs are searchable too: «glød» finds the treatments tagged behov-glod. data-search-words adds the words
        // people use for each need («aldring», «t-sone», «utslett») and the treatment's own intro, focus and «Passer for»
        // (snippets/laserlab-search-words.liquid).
        const raw = `${visibleCopy} ${labels.join(' ')} ${needs.map(need => this.needWords[need] || '').join(' ')} ${card.dataset.searchWords || ''}`;
        const text = this.normalize(raw);
        const price = card.dataset.price ? Number(card.dataset.price) : NaN;
        return {
          card, index, raw, text,
          words: text.split(' '),
          groups: (card.dataset.filterGroups || '').split('|').filter(Boolean),
          needs,
          primary: card.dataset.needPrimary || '',
          price: Number.isFinite(price) ? price : null
        };
      });
      this.orderKey = this.cards.map(card => card.index).join(',');

      const pressedType = this.typeChips.find(chip => chip.getAttribute('aria-pressed') === 'true');
      this.state = { type: pressedType?.dataset.filterTypeChip || 'all', need: '', query: '', sort: 'recommended' };
      this.abort = new AbortController();
      const on = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: this.abort.signal });
      // Below this width the search is a button in the result line that opens the field there; desktop shows the field (CSS).
      this.compactMedia = window.matchMedia('(max-width: 1023px)');

      this.needChips.forEach(chip => on(chip, 'click', () => {
        const need = chip.dataset.filterNeedChip;
        this.state.need = this.state.need === need ? '' : need;
        this.commit();
        this.afterChoice(chip);
      }));
      this.typeChips.forEach(chip => on(chip, 'click', () => {
        const type = chip.dataset.filterTypeChip;
        this.state.type = this.state.type === type ? 'all' : type;
        this.commit();
        // A removed type chip disappears (Laser facials); keep focus in the row.
        if (chip.hidden) (this.needChips.find(item => !item.disabled) || this.searchToggle || this.input)?.focus({ preventScroll: true });
        this.afterChoice(chip.hidden ? null : chip);
      }));
      on(this.sortSelect, 'change', () => {
        this.state.sort = Object.hasOwn(this.sortCaptions, this.sortSelect.value) ? this.sortSelect.value : 'recommended';
        this.commit();
        this.scrollToResults();
      });

      if (this.input) {
        on(this.input, 'input', () => { this.state.query = this.input.value; this.commit({ quiet: true }); });
        on(this.input, 'search', () => { this.state.query = this.input.value; this.commit({ quiet: true }); });
        on(this.input, 'keydown', event => {
          if (event.key !== 'Escape' || !this.isSearchOpen()) return;
          event.preventDefault();
          event.stopPropagation();
          this.closeSearch();
        });
        on(this.form, 'submit', event => {
          event.preventDefault();
          this.state.query = this.input.value;
          this.commit();
          // Phones: Enter closes the keyboard so the results are visible.
          if (window.matchMedia('(max-width: 767px)').matches) this.input.blur();
        });
        on(this.clear, 'click', event => {
          event.preventDefault();
          if (this.isSearchOpen()) { this.closeSearch(); return; }
          this.input.value = '';
          this.state.query = '';
          this.commit();
          this.input.focus({ preventScroll: true });
        });
        on(this.searchToggle, 'click', () => this.openSearch());
        // The search examples show only while the field is in use. They fold away a moment after focus leaves,
        // so a tap below never lands on content that has just moved up.
        on(this.form, 'focusin', () => { clearTimeout(this.hintTimer); this.searchHost?.classList.add('is-hinting'); });
        on(this.form, 'focusout', () => {
          clearTimeout(this.hintTimer);
          this.hintTimer = setTimeout(() => { if (!this.form.contains(document.activeElement)) this.searchHost?.classList.remove('is-hinting'); }, 250);
          // An opened but empty search folds back when focus leaves it.
          requestAnimationFrame(() => {
            if (!this.isSearchOpen() || this.form.contains(document.activeElement) || this.state.query.trim()) return;
            this.setSearchOpen(false);
            this.render();
          });
        });
        on(this.compactMedia, 'change', () => {
          this.syncControls();
          this.render();
        });
        on(this.phoneMedia, 'change', () => this.render());
        // The spelling help loads after the page; a pending search is tried again with it.
        on(document, 'laserlab:search-terms', () => { if (this.state.query.trim()) this.render(); });
      }
      on(this, 'click', event => {
        if (event.target instanceof Element && event.target.closest('[data-filter-reset]')) this.clearFilters();
      });

      // The chip row stays under the header while the list scrolls; once it is stuck it casts the header's shadow.
      if (this.bar) {
        const onScroll = () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            const top = parseFloat(getComputedStyle(this.bar).top) || 0;
            this.bar.classList.toggle('is-stuck', window.scrollY > 0 && this.bar.getBoundingClientRect().top <= top + .5);
          });
        };
        on(window, 'scroll', onScroll, { passive: true });
        on(window, 'resize', onScroll, { passive: true });
        onScroll();
      }

      if (this.syncURL) {
        on(window, 'popstate', () => this.readLocation());
        // Where the list was left, so Back to a filtered list can return to the same place (see restoreScroll).
        on(window, 'pagehide', () => {
          try { sessionStorage.setItem(this.scrollKey(), String(Math.round(window.scrollY))); } catch (_) { /* Private mode: the browser's own restore applies. */ }
        });
      }
      // Back from the page cache reads the address again; a normal load only puts the controls back in line with it
      // (browsers may restore form fields), so a search typed while the page loads is kept.
      on(window, 'pageshow', event => { if (event.persisted || !this.syncURL) this.readLocation(); else this.syncControls(); });

      this.classList.add('is-enhanced');
      this.initialized = true;
      this.readLocation();
      this.revealChip(this.needChips.find(chip => chip.getAttribute('aria-pressed') === 'true'));
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      clearTimeout(this.writeTimer);
      clearTimeout(this.hintTimer);
      cancelAnimationFrame(this.scrollFrame);
      this.abort?.abort();
      this.initialized = false;
    }

    /* ---------- State and address ---------- */

    readLocation() {
      const state = { ...this.state };
      if (this.syncURL) {
        let params;
        try { params = new URLSearchParams(location.search); } catch (_) { params = new URLSearchParams(); }
        state.query = this.input ? params.get('sok') || '' : '';
        const need = (params.get('behov') || '').toLocaleLowerCase('nb-NO');
        state.need = this.needs.includes(need) ? need : '';
        const sort = params.get('sorter') || 'recommended';
        state.sort = this.sortSelect && Object.hasOwn(this.sortCaptions, sort) ? sort : 'recommended';
        const type = this.typeFromPath() || this.typeFromParam(params);
        if (type) state.type = type;
        else if (this.typeAllURL && this.pathOf(this.typeAllURL) === this.pathOf(location.href)) state.type = 'all';
      } else {
        state.query = this.input?.value || '';
        state.sort = this.sortSelect?.value || 'recommended';
      }
      this.state = state;
      this.syncControls();
      this.render();
      this.writeState();
      this.removeAttribute('data-restoring');
      this.restoreScroll();
    }

    scrollKey() {
      return `ll-list-scroll:${location.pathname}${location.search}`;
    }

    /* Back to a filtered list that the browser loads again (no page cache): the cards are only in order once the
       choices are applied, so the inline script turned the browser's own restore off and the position is set here. */
    restoreScroll() {
      if (this.scrollRestored || !this.syncURL) return;
      this.scrollRestored = true;
      try {
        if (history.scrollRestoration !== 'manual') return;
        const saved = sessionStorage.getItem(this.scrollKey());
        if (saved !== null) window.scrollTo(0, Number(saved) || 0);
        history.scrollRestoration = 'auto';
      } catch (_) { /* Nothing to restore. */ }
    }

    // A Laser facials type is the page itself (/collections/laser-facials-pico); its chip carries that address.
    typeFromPath() {
      const here = this.pathOf(location.href);
      const match = this.typeChips.find(chip => chip.dataset.url && this.pathOf(chip.dataset.url) === here);
      return match ? match.dataset.filterTypeChip : '';
    }

    // A category without an address of its own (a Skinlab category) is ?kategori=<name>, so a link can open the list
    // on it (Om Laserlab links each Skinlab category this way).
    typeFromParam(params) {
      const wanted = (params.get('kategori') || '').toLocaleLowerCase('nb-NO');
      const match = wanted && this.typeChips.find(chip => !chip.dataset.url && chip.dataset.filterTypeChip === wanted);
      return match ? match.dataset.filterTypeChip : '';
    }

    pathOf(url) {
      try { return new URL(url, location.href).pathname.replace(/\/+$/, '') || '/'; } catch (_) { return ''; }
    }

    /* Writes the state to the address without adding history steps. Unknown values have already been dropped. */
    writeState() {
      if (this.syncURL) {
        try {
          const url = new URL(location.href);
          const set = (name, value) => { if (value) url.searchParams.set(name, value); else url.searchParams.delete(name); };
          set('behov', this.state.need);
          set('sok', this.state.query.trim());
          set('sorter', this.state.sort === 'recommended' ? '' : this.state.sort);
          const chip = this.typeChips.find(item => item.dataset.filterTypeChip === this.state.type);
          set('kategori', chip && !chip.dataset.url && this.state.type !== 'all' ? this.state.type : '');
          const path = chip?.dataset.url ? this.pathOf(chip.dataset.url) : (this.state.type === 'all' && this.typeAllURL ? this.pathOf(this.typeAllURL) : '');
          if (path) url.pathname = path;
          if (url.href !== location.href) history.replaceState(history.state, '', url.href);
        } catch (_) { /* The list still works without an addressable URL. */ }
      }
      this.updateTabs();
    }

    /* The chosen need follows the customer to the categories that have it, and back to Alle behandlinger.
       Each tab lists its category's needs (data-tab-needs, «*» = all). */
    updateTabs() {
      const need = this.state.need;
      this.tabs.forEach(link => {
        let url;
        try { url = new URL(link.getAttribute('href'), location.href); } catch (_) { return; }
        if (url.origin !== location.origin) return;
        const needs = link.dataset.tabNeeds === '*' ? null : (link.dataset.tabNeeds || '').split(' ');
        if (need && (!needs || needs.includes(need))) url.searchParams.set('behov', need);
        else url.searchParams.delete('behov');
        const next = `${url.pathname}${url.search}${url.hash}`;
        if (link.getAttribute('href') !== next) link.setAttribute('href', next);
      });
    }

    /* Applies a change: cards, counts, controls and the address. quiet = typing (the address follows after a pause). */
    commit({ quiet = false } = {}) {
      this.render();
      clearTimeout(this.writeTimer);
      if (quiet) this.writeTimer = setTimeout(() => this.writeState(), 400);
      else this.writeState();
    }

    /* After a choice further down the list, the new result starts right under the sticky row. */
    afterChoice(chip) {
      this.revealChip(chip);
      this.scrollToResults();
    }

    scrollToResults() {
      if (!this.bar?.classList.contains('is-stuck') || !this.meta) return;
      const stickyTop = parseFloat(getComputedStyle(this.bar).top) || 0;
      const target = Math.round(this.meta.getBoundingClientRect().top + window.scrollY - stickyTop - this.bar.offsetHeight);
      if (window.scrollY > target + 1) window.scrollTo({ top: target, behavior: 'auto' });
    }

    // Keeps a chip fully in view in the swipe row without moving the page.
    revealChip(chip) {
      const list = this.scroller;
      if (!chip || !list || chip.hidden || list.scrollWidth <= list.clientWidth) return;
      const chipBox = chip.getBoundingClientRect();
      const listBox = list.getBoundingClientRect();
      if (chipBox.left < listBox.left) list.scrollLeft -= listBox.left - chipBox.left + 16;
      else if (chipBox.right > listBox.right) list.scrollLeft += chipBox.right - listBox.right + 16;
    }

    clearFilters() {
      this.state = { ...this.state, type: 'all', need: '', query: '' };
      if (this.input) this.input.value = '';
      this.setSearchOpen(false);
      this.syncControls();
      this.commit();
      (this.needChips[0] || this.searchToggle || this.input)?.focus({ preventScroll: true });
    }

    /* Controls follow the state (also after the browser restores form fields on Back or reload). */
    syncControls() {
      if (this.input && this.input.value !== this.state.query) this.input.value = this.state.query;
      if (this.sortSelect) this.sortSelect.value = this.state.sort;
      if (this.searchToggle) {
        if (this.state.query.trim()) this.setSearchOpen(true);
        else if (document.activeElement !== this.input) this.setSearchOpen(false);
      }
    }

    /* ---------- Search in the result line (opens in the line on phones and tablets) ---------- */

    isSearchOpen() {
      return Boolean(this.searchToggle && this.searchHost?.classList.contains('is-searching'));
    }

    setSearchOpen(open) {
      if (!this.searchToggle || !this.searchHost) return;
      this.searchHost.classList.toggle('is-searching', open);
      this.searchToggle.setAttribute('aria-expanded', String(open));
    }

    openSearch() {
      this.setSearchOpen(true);
      this.render();
      this.input?.focus({ preventScroll: true });
    }

    closeSearch() {
      if (this.input) this.input.value = '';
      this.state.query = '';
      this.setSearchOpen(false);
      this.commit();
      if (this.searchToggle?.getClientRects().length) this.searchToggle.focus({ preventScroll: true });
      else this.input?.focus({ preventScroll: true });
    }

    /* ---------- Matching, counts and order ---------- */

    normalize(value) {
      return String(value).toLocaleLowerCase('nb-NO')
        .replace(/æ/g, 'ae').replace(/ø/g, 'o')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim();
    }

    // A term matches inside card text, or by its stem ("rynker" -> "rynkebehandling").
    // A term finds a word that begins with it (so a word still being typed already finds «pico»), or an inflection or
    // compound of it (stems). Never letters inside a word: «ringer» must not find «pigmentforandringer» (28.09).
    termMatches(card, term) {
      if (card.words.some(word => word.startsWith(term))) return true;
      const search = window.LaserlabSearchTerms;
      return Boolean(search) && search.stems(term).some(stem => card.words.some(word => search.stemMatches(word, stem)));
    }

    // 0 when every term is a whole word on the card (or that word with a short ending: «rynke», «rynker»), else 1.
    // Whole-word answers come first: «akne» shows Acne Advanced before the treatments for «aknearr».
    queryTier(card, terms) {
      const search = window.LaserlabSearchTerms;
      const whole = (word, value) => word === value || (word.startsWith(value) && word.length - value.length <= 2);
      const exact = term => card.words.some(word => whole(word, term)) || (Boolean(search) && search.stems(term).some(stem => card.words.some(word => whole(word, stem))));
      return terms.every(exact) ? 0 : 1;
    }

    // Misspelled or English words ("brazillian", "armpit") fall back to the words on these cards.
    searchTerms(pool) {
      const query = this.state.query.trim();
      const terms = this.normalize(query).split(' ').filter(Boolean);
      const search = window.LaserlabSearchTerms;
      if (!terms.length || !search || pool.some(card => terms.every(term => this.termMatches(card, term)))) return { terms, corrected: '' };
      if (!this.searchIndex) this.searchIndex = search.createIndex(this.cards.map(card => card.raw));
      const correction = search.correct(query, this.searchIndex);
      if (!correction.changed) return { terms, corrected: '' };
      const corrected = this.normalize(correction.query).split(' ').filter(Boolean);
      return pool.some(card => corrected.every(term => this.termMatches(card, term))) ? { terms: corrected, corrected: correction.query } : { terms, corrected: '' };
    }

    label(count) {
      return `${count} ${count === 1 ? 'behandling' : 'behandlinger'}`;
    }

    render() {
      const { type, need } = this.state;
      const inType = (card, value = type) => value === 'all' || card.groups.includes(value);
      const hasNeed = (card, value = need) => !value || card.needs.includes(value);
      const { terms, corrected } = this.searchTerms(this.cards.filter(card => inType(card) && hasNeed(card)));
      const matchesQuery = card => terms.every(term => this.termMatches(card, term));
      let matches = 0;
      this.queryTiers = new Map();
      this.cards.forEach(card => {
        const visible = inType(card) && hasNeed(card) && matchesQuery(card);
        card.card.hidden = !visible;
        if (visible) matches += 1;
        if (visible && terms.length) this.queryTiers.set(card, this.queryTier(card, terms));
      });

      // Counts per chip answer "what do I get if I choose this", given the other choices.
      const needPool = this.cards.filter(card => inType(card) && matchesQuery(card));
      const typePool = this.cards.filter(card => hasNeed(card) && matchesQuery(card));
      const setCount = (chip, count) => {
        const node = chip.querySelector('[data-filter-chip-count]');
        if (!node) return;
        const text = String(count);
        if (node.firstChild?.nodeType === Node.TEXT_NODE) { if (node.firstChild.nodeValue !== text) node.firstChild.nodeValue = text; }
        else node.prepend(text);
        const spoken = node.querySelector('.visually-hidden');
        if (spoken) spoken.textContent = ` ${count === 1 ? 'behandling' : 'behandlinger'}`;
      };
      this.needChips.forEach(chip => {
        const value = chip.dataset.filterNeedChip;
        const count = needPool.filter(card => card.needs.includes(value)).length;
        const pressed = value === need;
        chip.setAttribute('aria-pressed', String(pressed));
        chip.disabled = !pressed && count === 0;
        setCount(chip, count);
      });
      this.typeChips.forEach(chip => {
        const value = chip.dataset.filterTypeChip;
        const pressed = value === type;
        chip.setAttribute('aria-pressed', String(pressed));
        // A Laser facials type is only a chip while it is chosen (from its own page).
        if (chip.hasAttribute('data-chip-auto')) { chip.hidden = !pressed; return; }
        const count = typePool.filter(card => inType(card, value)).length;
        chip.disabled = !pressed && count === 0;
        setCount(chip, count);
      });
      this.bar?.classList.toggle('has-choice', Boolean(need) || type !== 'all');
      this.order();

      const active = Boolean(this.state.query.trim()) || type !== 'all' || Boolean(need);
      const consultGrid = this.consult?.parentElement;
      if (consultGrid) {
        const last = active || this.phoneMedia.matches;
        if (last && consultGrid.lastElementChild !== this.consult) consultGrid.appendChild(this.consult);
        if (!last && consultGrid.firstElementChild !== this.consult) consultGrid.prepend(this.consult);
      }
      const total = this.cards.length;
      const pageOnly = this.dataset.filterPageOnly === 'true' ? ' på denne siden' : '';
      const noun = total === 1 ? 'behandling' : 'behandlinger';
      this.count.textContent = active ? `${matches} av ${total} ${noun}${pageOnly}` : `${total} ${noun}${pageOnly}`;
      if (corrected) {
        const spoken = document.createElement('span');
        spoken.className = 'visually-hidden';
        spoken.textContent = `, treff for ${corrected}`;
        this.count.append(spoken);
      }
      if (this.hint) {
        this.hint.textContent = corrected ? `Viser treff for «${corrected}».` : this.hintText;
        this.hint.toggleAttribute('data-correction', Boolean(corrected));
      }
      if (this.clear) {
        const open = this.isSearchOpen();
        this.clear.hidden = !this.input.value && !open;
        this.clear.setAttribute('aria-label', open ? 'Lukk og tøm søket' : 'Tøm søket');
      }
      if (this.empty) this.empty.hidden = !active || matches > 0;
      if (this.sortCaption) this.sortCaption.textContent = this.sortCaptions[this.state.sort];
      // The sort icon in the row is filled while a sort other than Anbefalt is chosen.
      this.sortSelect?.closest('.ll-card-filter__sort')?.toggleAttribute('data-active', this.state.sort !== 'recommended');
    }

    /* Anbefalt keeps the clinic's own order (the collection's manual order). With a need chosen, the treatments whose
       card badge names that need come first. Prices use the lowest price; cards without a price go last. */
    order() {
      const { sort, need } = this.state;
      const tier = card => (need && card.primary === need ? 0 : 1);
      // While searching, the best answers lead (queryTier); otherwise the clinic's order.
      const relevance = card => this.queryTiers?.get(card) ?? 0;
      const sorted = [...this.cards].sort((a, b) => {
        if (sort === 'price-ascending') return ((a.price ?? Infinity) - (b.price ?? Infinity)) || a.index - b.index;
        if (sort === 'price-descending') return ((b.price ?? -Infinity) - (a.price ?? -Infinity)) || a.index - b.index;
        return relevance(a) - relevance(b) || tier(a) - tier(b) || a.index - b.index;
      });
      const key = sorted.map(card => card.index).join(',');
      if (key === this.orderKey) return;
      this.orderKey = key;
      sorted.forEach(({ card }) => card.parentElement?.appendChild(card));
    }
  });
}
